import { Button } from '@heroui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { GrCloudUpload } from 'react-icons/gr';
import { z } from 'zod';
import { cn, FormFileInput, FormSwitch, FormTextArea } from '@/core/ui';

const schema = z.object({
  file: z
    .file()
    .mime(['image/jpeg', 'image/png', 'image/gif', 'image/webp'], {
      message: 'Only .zip format is supported.',
    })
    .max(4 * 1024 * 1024, { message: `Max File size is 4MB.` })
    .refine((file) => file !== null && file !== undefined, {
      message: 'File is required',
    })
    .refine((file) => file?.size <= 4 * 1024 * 1024, {
      message: `Max File size is 4MB.`,
    }),
  caption: z.string().optional().nullable(),
  isSensitive: z.boolean(),
});

export const AssetUpload = ({ onSubmit }: { onSubmit: (data: z.infer<typeof schema>) => void }) => {
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      isSensitive: false,
    },
  });

  const onSubmitHandler: SubmitHandler<z.infer<typeof schema>> = (data) => {
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmitHandler)}>
      <div className='grid grid-cols-2 gap-4 items-center' onSubmit={handleSubmit(onSubmit)}>
        <FormFileInput
          control={control}
          name='file'
          options={{
            accept: {
              'image/jpeg': ['.jpg', '.jpeg'],
              'image/png': ['.png'],
              'image/gif': ['.gif'],
              'image/webp': ['.webp'],
            },
            maxSize: 4 * 1024 * 1024,
            maxFiles: 1,
          }}
          className='col-span-2'
        >
          <div className='flex flex-col items-center justify-center gap-2'>
            <div className='flex items-center justify-center size-14 rounded-full bg-default-200 mb-2'>
              <GrCloudUpload className='text-foreground w-8 h-8' />
            </div>

            <span className='text-sm text-foreground'>Drag & drop, or click to browse</span>
            <span className='text-xs text-foreground-400'>Supports: JPG, PNG, GIF, WEBP (Max 4MB).</span>
          </div>
        </FormFileInput>
        <FormTextArea
          control={control}
          name='caption'
          label='Caption'
          labelPlacement='outside'
          className='col-span-2'
          variant='faded'
          placeholder='Add a description for the image'
        />
        <FormSwitch
          control={control}
          name='isSensitive'
          className='col-span-2 w-full'
          classNames={{
            base: cn(
              'col-span-2 inline-flex flex-row-reverse w-full max-w-full bg-content2 items-center',
              'justify-between cursor-pointer rounded-lg gap-2 p-2 border-2 border-default-200',
              'data-[selected=true]:border-primary',
            ),
            // wrapper: 'p-0 h-4 overflow-visible',
            // thumb: cn(
            //   'w-6 h-6 border-2 shadow-lg',
            //   'group-data-[hover=true]:border-primary',
            //   //selected
            //   'group-data-[selected=true]:ms-6',
            //   // pressed
            //   'group-data-[pressed=true]:w-7',
            //   'group-data-pressed:group-data-selected:ms-4',
            // ),
          }}
        >
          <div className='flex flex-col justify-center'>
            <span className='text-sm font-medium text-foreground'>Mark as sensitive?</span>
            <span className='text-xs text-foreground-400'>This will render as blur in the app</span>
          </div>
        </FormSwitch>
      </div>

      <Button type='submit' isDisabled={!isValid} className='mt-8 w-full'>
        Submit
      </Button>
    </form>
  );
};
